import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Card, FormField, LogoMark, TextInput } from '@/components/ui';
import { login } from '@/shared/api/authApi';
import { useAuthStore } from '@/shared/stores/auth';
import { setLang, type Lang } from '@/shared/i18n';

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.set);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentLang = (i18n.language?.startsWith('he') ? 'he' : 'en') as Lang;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const result = await login({
        email,
        password,
        device_name: navigator.userAgent.slice(0, 100),
      });

      setAuth({
        token: result.token,
        user: result.user,
        persona: result.persona,
      });

      const next = `/${result.persona}`;
      navigate(next, { replace: true });
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        if (err.response?.status === 429) {
          setError(t('auth.login.rate_limited'));
        } else if (err.response?.status === 403) {
          setError(t('auth.login.blocked'));
        } else if (err.response?.status === 422) {
          setError(t('auth.login.invalid'));
        } else {
          setError(t('auth.login.invalid'));
        }
      } else {
        setError(t('common.error'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function toggleLang() {
    const next: Lang = currentLang === 'he' ? 'en' : 'he';
    setLang(next);
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--bg)',
        padding: 'var(--gap)',
      }}
    >
      <Card
        padded={false}
        style={{
          width: '100%',
          maxWidth: 420,
          padding: 'calc(var(--pad-card) * 1.5)',
        }}
      >
        <div className="stack">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            {/* ה-wordmark כבר אומר GoCall — טקסט שם לצידו היה כפילות. */}
            <LogoMark size={40} />
            <button
              type="button"
              className="btn ghost"
              onClick={toggleLang}
              aria-label="toggle language"
            >
              {currentLang === 'he' ? 'EN' : 'עב'}
            </button>
          </div>

          <h1 style={{ margin: 0, fontSize: 'var(--t-2xl)', fontWeight: 700 }}>
            {t('auth.login.title')}
          </h1>

          <form
            onSubmit={handleSubmit}
            className="stack"
            aria-label={t('auth.login.title')}
            noValidate
          >
            <FormField label={t('auth.login.email')} htmlFor="email" required>
              <TextInput
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                dir="ltr"
              />
            </FormField>

            <FormField label={t('auth.login.password')} htmlFor="password" required>
              <TextInput
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </FormField>

            {error && (
              <div
                role="alert"
                style={{
                  padding: 12,
                  background: 'var(--danger-soft)',
                  color: 'var(--danger)',
                  borderRadius: 'var(--r-2)',
                  fontSize: 'var(--t-sm)',
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn primary"
              disabled={submitting || !email || !password}
              style={{ height: 'var(--row-h)' }}
            >
              {submitting ? t('common.loading') : t('auth.login.submit')}
            </button>
          </form>
        </div>
      </Card>
    </div>
  );
}
